import type { SupabaseClient } from '@supabase/supabase-js'
import type { Entitlements, RegisteredCounter, TenantSettingsRow } from '@ims/data'
import { unwrap } from './errors.ts'

export type MemberRole = 'viewer' | 'waiter' | 'staff' | 'manager' | 'admin' | 'owner'

export interface TenantMembership {
  tenant_id: string
  role: MemberRole
  tenant: {
    id: string
    name: string
    slug: string
    is_active: boolean
    /**
     * Restaurant mode and business type, so the sidebar is right before the first sync
     * brings the settings row. Missing in lists cached by older apps.
     */
    settings?: Pick<TenantSettingsRow, 'restaurant' | 'business_type'> | null
  }
}

/** A change the server refused, from any device (sync_rejections_list, managers). */
export interface SyncRejection {
  tx_id: string
  installation_id: string
  counter_name: string | null
  user_email: string | null
  code: string
  message: string
  rejected_at: string
}

/** The signed-in account's room for businesses it owns (my_business_limit). */
export interface BusinessLimit {
  owned: number
  limit: number
  /** A paid plan on a business it owns would let this account own more. */
  more_with_plan: boolean
}

export interface SharedInvoice {
  invoice: Record<string, unknown>
  lines: Record<string, unknown>[]
  seller: Record<string, unknown> | null
  balance_paisa: number | null
}

/** Typed wrappers for the tenant RPCs (supabase/migrations/..._tenant_rpcs.sql). */
export function tenantApi(client: SupabaseClient) {
  const rpc = async <T>(fn: string, args: Record<string, unknown> = {}): Promise<T> =>
    unwrap(await client.rpc(fn, args)) as T

  return {
    /** Tenants the signed-in user belongs to, with their role. */
    myTenants: async (): Promise<TenantMembership[]> => {
      // The locally stored session (not getUser, which needs the network and
      // would make an offline start look like "no businesses"). Network
      // failures below throw, so callers keep their cached list.
      const { data: auth } = await client.auth.getSession()
      if (!auth.session) return []
      return unwrap(
        await client
          .from('memberships')
          .select(
            'tenant_id, role, tenant:tenants(id, name, slug, is_active, settings:tenant_settings(restaurant, business_type))',
          )
          .eq('user_id', auth.session.user.id),
      ) as unknown as TenantMembership[]
    },
    /** Makes a business; `registration` is what set-up asked about PAN or VAT. */
    createTenant: (
      name: string,
      slug: string,
      businessType = 'general',
      registration: { taxRegistration?: 'pan' | 'vat'; panVatNumber?: string | null } = {},
    ) =>
      rpc<{ tenant_id: string }>('create_tenant', {
        p_name: name,
        p_slug: slug,
        p_business_type: businessType,
        p_tax_registration: registration.taxRegistration ?? 'pan',
        p_pan_vat_number: registration.panVatNumber || null,
      }),
    /**
     * Approves or rejects a document waiting for approval (decide_document, online). The
     * server checks it is this person's step, role and own limit; a rejection says why.
     */
    decideDocument: (
      tenantId: string,
      docType: 'purchase_order' | 'requisition',
      docId: string,
      decision: 'approved' | 'rejected',
      note: string | null,
    ) =>
      rpc<{ status: string; level: number }>('decide_document', {
        p_tenant_id: tenantId,
        p_doc_type: docType,
        p_doc_id: docId,
        p_decision: decision,
        p_note: note,
      }),
    /** How much one person may approve on top of their role; null: none of their own. */
    setApprovalLimit: (tenantId: string, userId: string, limitPaisa: number | null) =>
      rpc<null>('set_member_approval_limit', {
        p_tenant_id: tenantId,
        p_user_id: userId,
        p_limit: limitPaisa,
      }),
    /** The signed-in person's own approval limit in this business (null: none). */
    myApprovalLimit: async (tenantId: string): Promise<number | null> => {
      const { data: auth } = await client.auth.getSession()
      if (!auth.session) return null
      const row = unwrap(
        await client
          .from('memberships')
          .select('approval_limit_paisa')
          .eq('tenant_id', tenantId)
          .eq('user_id', auth.session.user.id)
          .maybeSingle(),
      ) as { approval_limit_paisa: number | null } | null
      return row?.approval_limit_paisa ?? null
    },
    /** How many businesses this account owns and may own; create_tenant enforces it. */
    businessLimit: () => rpc<BusinessLimit>('my_business_limit'),
    createInvite: (tenantId: string, email: string, role: Exclude<MemberRole, 'owner'>) =>
      rpc<{ invite_id: string; token: string }>('create_invite', {
        p_tenant_id: tenantId,
        p_email: email,
        p_role: role,
      }),
    revokeInvite: (inviteId: string) => rpc<void>('revoke_invite', { p_invite_id: inviteId }),
    acceptInvite: (token: string) =>
      rpc<{ tenant_id: string; role: MemberRole }>('accept_invite', { p_token: token }),
    setMemberRole: (tenantId: string, userId: string, role: MemberRole) =>
      rpc<void>('set_member_role', { p_tenant_id: tenantId, p_user_id: userId, p_role: role }),
    removeMember: (tenantId: string, userId: string) =>
      rpc<void>('remove_member', { p_tenant_id: tenantId, p_user_id: userId }),
    registerCounter: (tenantId: string, installationId: string, name?: string) =>
      rpc<RegisteredCounter>('register_counter', {
        p_tenant_id: tenantId,
        p_installation_id: installationId,
        p_name: name ?? null,
      }),
    deactivateCounter: (tenantId: string, counterId: string) =>
      rpc<void>('deactivate_counter', { p_tenant_id: tenantId, p_counter_id: counterId }),
    /** Each table's QR link token, making the missing ones (managers). */
    tableQrCodes: (tenantId: string) =>
      rpc<{ table_id: string; name: string; token: string }[]>('table_qr_codes', {
        p_tenant_id: tenantId,
      }),
    /** One table's QR link token (waiters and up), made if it is missing. */
    tableQrCode: (tenantId: string, tableId: string) =>
      rpc<{ token: string }>('table_qr_code', { p_tenant_id: tenantId, p_table_id: tableId }),
    /** A new link for one table; the old one stops working. */
    regenerateTableQr: (tenantId: string, tableId: string) =>
      rpc<{ token: string }>('regenerate_table_qr', {
        p_tenant_id: tenantId,
        p_table_id: tableId,
      }),
    entitlements: (tenantId: string) =>
      rpc<Entitlements>('get_entitlements', { p_tenant_id: tenantId }),
    createInvoiceShare: (tenantId: string, invoiceId: string) =>
      rpc<{ token: string }>('create_invoice_share', {
        p_tenant_id: tenantId,
        p_invoice_id: invoiceId,
      }),
    revokeInvoiceShare: (tenantId: string, invoiceId: string) =>
      rpc<void>('revoke_invoice_share', { p_tenant_id: tenantId, p_invoice_id: invoiceId }),
    /** Changes the server refused on any device, last 30 days (managers). */
    syncRejections: (tenantId: string) =>
      rpc<SyncRejection[]>('sync_rejections_list', { p_tenant_id: tenantId }),
    /** Hides a rejection from the managers' list (its device, or a manager). */
    dismissSyncRejection: (tenantId: string, txId: string) =>
      rpc<void>('sync_rejection_dismiss', { p_tenant_id: tenantId, p_tx_id: txId }),
    /** Anonymous: the token is the credential. Null when invalid or revoked. */
    sharedInvoice: (token: string) =>
      rpc<SharedInvoice | null>('get_shared_invoice', { p_token: token }),
  }
}

export type TenantApi = ReturnType<typeof tenantApi>
